/**
 * Device ID Management Utility for KDS
 * Generates and persists unique device identifiers to prevent duplicate events
 * across multiple devices viewing the same production center
 */

const DEVICE_ID_KEY = "kds_device_id";

/**
 * Generate a UUID v4 format device ID
 * @returns {string} UUID format device ID
 */
function generateDeviceId() {
  // UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get existing device ID from localStorage or create a new one
 * This ensures each device/browser has a unique, persistent identifier
 * @returns {string} Device ID
 */
export function getOrCreateDeviceId() {
  // Handle SSR/Node environment
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    console.warn(
      "[KDS] localStorage not available, generating temporary device ID"
    );
    return generateDeviceId();
  }

  try {
    // Try to get existing device ID
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);

    if (deviceId) {
      console.log("[KDS] Using existing device ID:", deviceId);
      return deviceId;
    }

    // Generate new device ID if none exists
    deviceId = generateDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
    console.log("[KDS] Generated new device ID:", deviceId);

    return deviceId;
  } catch (error) {
    console.error("[KDS] Error accessing localStorage for device ID:", error);
    // Fallback to generating temporary ID
    return generateDeviceId();
  }
}

/**
 * Manually set a custom device ID (useful for pre-labeled tablets)
 * Example: setDeviceId('kitchen-tablet-1')
 * @param {string} customId - Custom device identifier
 */
export function setDeviceId(customId) {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    console.warn("[KDS] localStorage not available, cannot set device ID");
    return;
  }

  try {
    localStorage.setItem(DEVICE_ID_KEY, customId);
    console.log("[KDS] Device ID manually set to:", customId);
  } catch (error) {
    console.error("[KDS] Error setting device ID:", error);
  }
}

/**
 * Clear the stored device ID (useful for testing or device reset)
 */
export function clearDeviceId() {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    console.warn("[KDS] localStorage not available");
    return;
  }

  try {
    localStorage.removeItem(DEVICE_ID_KEY);
    console.log("[KDS] Device ID cleared");
  } catch (error) {
    console.error("[KDS] Error clearing device ID:", error);
  }
}

/**
 * Get the current device ID without creating a new one
 * @returns {string|null} Current device ID or null if not set
 */
export function getCurrentDeviceId() {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return null;
  }

  try {
    return localStorage.getItem(DEVICE_ID_KEY);
  } catch (error) {
    console.error("[KDS] Error getting device ID:", error);
    return null;
  }
}
